import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useRealtimeSubscription } from "@/hooks/use-realtime";
import { Search, Plus, Mail, Phone, Building2, Edit, Trash2, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";

export const ContactsList = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [formData, setFormData] = useState({
    customer_id: "",
    lead_status: "new",
    lead_source: "",
    industry: "",
    company_size: "",
    website: "",
    tags: [] as string[],
  });
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Fetch all contacts - both CRM contacts and auto-sync from clients
  const { data: contacts, isLoading } = useQuery({
    queryKey: ["crm-contacts"],
    queryFn: async () => {
      // Get existing CRM contacts
      const { data: crmContacts, error: crmError } = await supabase
        .from("crm_contacts")
        .select(`
          *,
          customers (
            id,
            name,
            email,
            phone,
            company,
            address
          ),
          profiles!crm_contacts_assigned_to_fkey (
            id,
            first_name,
            last_name,
            email
          )
        `)
        .order("created_at", { ascending: false });

      if (crmError) throw crmError;

      // Get all customers (manually added)
      const { data: allCustomers, error: custError } = await supabase
        .from("customers")
        .select("*");

      if (custError) throw custError;

      // Get all client portal users
      const { data: clientUsers, error: clientError } = await supabase
        .from("profiles")
        .select("*")
        .eq("user_type", "client");

      if (clientError) throw clientError;

      // Combine all sources
      const existingContactEmails = new Set(
        crmContacts?.map(c => c.customers?.email?.toLowerCase()).filter(Boolean) || []
      );

      // Add customers not yet in CRM
      const customersNotInCRM = allCustomers?.filter(
        c => c.email && !existingContactEmails.has(c.email.toLowerCase())
      ) || [];

      // Add client portal users not yet in CRM
      const clientsNotInCRM = clientUsers?.filter(
        c => c.email && !existingContactEmails.has(c.email.toLowerCase())
      ) || [];

      // Format all into unified structure
      const formattedContacts = [
        ...(crmContacts || []).map(c => ({
          ...c,
          auto_generated: false,
        })),
        // Auto-add customers as contacts
        ...customersNotInCRM.map(customer => ({
          id: `customer-${customer.id}`,
          customer_id: customer.id,
          lead_status: 'new' as const,
          lead_source: 'manual',
          industry: null,
          company_size: null,
          website: null,
          tags: [] as string[],
          created_at: customer.created_at,
          auto_generated: true,
          customers: customer,
          profiles: null,
        })),
        // Auto-add client portal users as contacts
        ...clientsNotInCRM.map(client => ({
          id: `profile-${client.id}`,
          customer_id: null,
          lead_status: 'new' as const,
          lead_source: 'client_portal',
          industry: null,
          company_size: null,
          website: null,
          tags: [] as string[],
          created_at: client.created_at,
          auto_generated: true,
          customers: {
            id: client.id,
            name: `${client.first_name || ''} ${client.last_name || ''}`.trim() || 'Client User',
            email: client.email,
            phone: null,
            company: null,
            address: null,
          },
          profiles: null,
        })),
      ];

      return formattedContacts;
    },
  });

  // Fetch customers for dropdown
  const { data: customers } = useQuery({
    queryKey: ["customers"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("customers")
        .select("*")
        .order("name");

      if (error) throw error;
      return data;
    },
  });

  // Realtime subscriptions
  useRealtimeSubscription("crm_contacts", "crm-contacts");
  useRealtimeSubscription("customers", "customers");

  // Create contact mutation
  const createMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { error } = await supabase
        .from("crm_contacts")
        .insert([{
          ...data,
          created_by: user.id,
        }]);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["crm-contacts"] });
      toast.success("Contact created successfully");
      setIsCreateOpen(false);
      setFormData({
        customer_id: "",
        lead_status: "new",
        lead_source: "",
        industry: "",
        company_size: "",
        website: "",
        tags: [],
      });
    },
    onError: (error) => {
      toast.error("Failed to create contact: " + error.message);
    },
  });

  // Delete contact mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("crm_contacts")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["crm-contacts"] });
      toast.success("Contact deleted");
    },
    onError: (error) => {
      toast.error("Failed to delete contact: " + error.message);
    },
  });

  const filteredContacts = contacts?.filter((contact) => {
    const searchLower = searchQuery.toLowerCase();
    const customer = contact.customers;
    return (
      customer?.name?.toLowerCase().includes(searchLower) ||
      customer?.email?.toLowerCase().includes(searchLower) ||
      customer?.company?.toLowerCase().includes(searchLower) ||
      contact.industry?.toLowerCase().includes(searchLower)
    );
  });

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      new: "bg-blue-500/10 text-blue-500 border-blue-500/20",
      contacted: "bg-yellow-500/10 text-yellow-500 border-yellow-500/20",
      qualified: "bg-purple-500/10 text-purple-500 border-purple-500/20",
      converted: "bg-green-500/10 text-green-500 border-green-500/20",
      lost: "bg-red-500/10 text-red-500 border-red-500/20",
    };
    return colors[status] || "bg-muted";
  };

  if (isLoading) {
    return <div className="text-center py-8 text-muted-foreground">Loading contacts...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search contacts..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Add Contact
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Add New Contact</DialogTitle>
            </DialogHeader>
            <div className="grid gap-4">
              <div className="grid gap-2">
                <Label>Customer *</Label>
                <Select
                  value={formData.customer_id}
                  onValueChange={(value) => setFormData({ ...formData, customer_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select customer" />
                  </SelectTrigger>
                  <SelectContent>
                    {customers?.map((customer) => (
                      <SelectItem key={customer.id} value={customer.id}>
                        {customer.name} {customer.email && `(${customer.email})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label>Lead Status</Label>
                  <Select
                    value={formData.lead_status}
                    onValueChange={(value) => setFormData({ ...formData, lead_status: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="contacted">Contacted</SelectItem>
                      <SelectItem value="qualified">Qualified</SelectItem>
                      <SelectItem value="converted">Converted</SelectItem>
                      <SelectItem value="lost">Lost</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid gap-2">
                  <Label>Lead Source</Label>
                  <Input
                    placeholder="e.g., Website, Referral"
                    value={formData.lead_source}
                    onChange={(e) => setFormData({ ...formData, lead_source: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label>Industry</Label>
                  <Input
                    placeholder="e.g., Technology"
                    value={formData.industry}
                    onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                  />
                </div>

                <div className="grid gap-2">
                  <Label>Company Size</Label>
                  <Select
                    value={formData.company_size}
                    onValueChange={(value) => setFormData({ ...formData, company_size: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select size" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1-10">1-10</SelectItem>
                      <SelectItem value="11-50">11-50</SelectItem>
                      <SelectItem value="51-200">51-200</SelectItem>
                      <SelectItem value="201-500">201-500</SelectItem>
                      <SelectItem value="500+">500+</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid gap-2">
                <Label>Website</Label>
                <Input
                  type="url"
                  placeholder="https://example.com"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                />
              </div>

              <Button onClick={() => createMutation.mutate(formData)} disabled={!formData.customer_id}>
                Create Contact
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-4">
        {filteredContacts && filteredContacts.length > 0 ? (
          filteredContacts.map((contact) => {
            const customer = contact.customers;
            const assignedTo = contact.profiles;

            return (
              <Card key={contact.id} className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4 flex-1">
                    <Avatar className="h-12 w-12">
                      <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                        {customer?.name?.split(' ').map(n => n[0]).join('').toUpperCase() || '?'}
                      </AvatarFallback>
                    </Avatar>
                    
                    <div className="flex-1 space-y-3">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-lg">{customer?.name || 'Unknown'}</h3>
                          <Badge className={getStatusColor(contact.lead_status)}>
                            {contact.lead_status}
                          </Badge>
                        </div>
                        {customer?.company && (
                          <p className="text-sm text-muted-foreground flex items-center gap-1">
                            <Building2 className="h-3 w-3" />
                            {customer.company}
                          </p>
                        )}
                      </div>

                      <div className="grid grid-cols-2 gap-2 text-sm">
                        {customer?.email && (
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Mail className="h-4 w-4" />
                            <a href={`mailto:${customer.email}`} className="hover:text-primary">
                              {customer.email}
                            </a>
                          </div>
                        )}
                        {customer?.phone && (
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Phone className="h-4 w-4" />
                            <a href={`tel:${customer.phone}`} className="hover:text-primary">
                              {customer.phone}
                            </a>
                          </div>
                        )}
                        {contact.industry && (
                          <div className="text-muted-foreground">
                            Industry: {contact.industry}
                          </div>
                        )}
                        {contact.company_size && (
                          <div className="text-muted-foreground">
                            Size: {contact.company_size} employees
                          </div>
                        )}
                        {contact.lead_source && (
                          <div className="text-muted-foreground">
                            Source: {contact.lead_source}
                          </div>
                        )}
                        {assignedTo && (
                          <div className="text-muted-foreground">
                            Assigned: {assignedTo.first_name} {assignedTo.last_name}
                          </div>
                        )}
                      </div>

                      {contact.website && (
                        <a 
                          href={contact.website} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-sm text-primary hover:underline inline-flex items-center gap-1"
                        >
                          Visit Website <ExternalLink className="h-3 w-3" />
                        </a>
                      )}

                      {contact.tags && contact.tags.length > 0 && (
                        <div className="flex gap-2 flex-wrap">
                          {contact.tags.map((tag, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      )}

                      {contact.auto_generated && (
                        <Badge variant="secondary" className="text-xs">
                          {contact.lead_source === 'client_portal' ? 'Auto-synced from Client Portal' : 'Auto-synced from Customers'}
                        </Badge>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/dashboard/clients`)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    {!contact.auto_generated && (
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => {
                          if (confirm('Delete this contact?')) {
                            deleteMutation.mutate(contact.id);
                          }
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            );
          })
        ) : (
          <Card className="p-12 text-center">
            <p className="text-muted-foreground">
              {searchQuery ? "No contacts found matching your search" : "No contacts yet. Add your first contact!"}
            </p>
          </Card>
        )}
      </div>
    </div>
  );
};